import { useCallback, useMemo, useState } from 'react';

import { GridCallbackDetails, GridFilterModel } from '@mui/x-data-grid';
import { useSearchParams } from 'react-router-dom';

import { queryParameters } from '~constants';
import { useUrlSearchParamsOrganisation } from '~features/organisations';

/**
 *
 * @param defaultFilterModel
 * @param disableUrlSync
 * @returns
 */
const useUrlSearchParamsFiltering = (
	defaultFilterModel?: GridFilterModel,
	disableUrlSync = false
): [GridFilterModel, (model: GridFilterModel, details: GridCallbackDetails) => void] => {
	const [searchParams, setSearchParams] = useSearchParams();

	const [organisation, setOrganisation] = useUrlSearchParamsOrganisation();

	const searchQueryParam = searchParams.get(queryParameters.SearchQuery);

	const urlFilterModel = useMemo<GridFilterModel>(
		() => ({
			items:
				organisation != null ?
					[
						{
							field: 'organisation',
							operator: 'contains',
							value: organisation,
						},
					]
				:	[],
			quickFilterValues: [searchQueryParam],
		}),
		[searchQueryParam, organisation]
	);

	const [internalFilterModel, setInternalFilterModel] =
		useState<GridFilterModel>(defaultFilterModel);

	const setFilterModel = useCallback(
		(model: GridFilterModel) => {
			if (disableUrlSync) {
				setInternalFilterModel(model);
				return;
			}

			const newOrganisation = model.items.find((el) => el.field === 'organisation')?.value;
			if (organisation?.id !== newOrganisation?.id) {
				setOrganisation(newOrganisation ?? null);
			}

			const newSearchQuery = model.quickFilterValues?.join(' ') || null;
			if (searchParams.get(queryParameters.SearchQuery) != newSearchQuery) {
				setSearchParams(
					(prev) => {
						if (model.quickFilterValues?.[0] != '' && model.quickFilterValues?.[0] != null) {
							prev.set(queryParameters.SearchQuery, model.quickFilterValues[0]);
						} else {
							prev.delete(queryParameters.SearchQuery);
						}

						return prev;
					},
					{ replace: true }
				);
			}
		},
		[disableUrlSync, setSearchParams, setOrganisation, organisation?.id, searchParams]
	);

	return [!disableUrlSync ? urlFilterModel : internalFilterModel, setFilterModel];
};

export default useUrlSearchParamsFiltering;
